import createBrandConfigGenerator from './createBrandConfigGenerator';
import * as colors from '../colors';
import { SortingOptions } from '../constants';

export const brandClientConfig = {
    /**
     * GENERAL CONFIGS
     */

    allowSignIn: false,
    analyticsName: undefined,
    directoryId: '-1',
    faviconUri: '',
    formalName: 'Zocdoc',

    ga: {
        // NOTE: ga.domains MUST always contain www.zocdoc.com in order for Google Analytics to work
        domains: ['www.zocdoc.com'],
    },

    googleTagManager: undefined,
    languages: null,
    strategicIds: [],

    /**
     * GLOBAL COMPONENTS
     */

    components: {
        AppointmentPicker: {
            config: {
                showExistingPatientsLink: false,
                showPhoneNumberIfNoAvailability: false,
            },

            header: {
                bgColor: colors.navy,
                textColor: colors.white,
            },

            slot: {
                bgColor: colors.yellow,
                bgColorEmpty: 'transparent',
                bgColorHover: colors.navy,
                bgColorActive: colors.green,
                bgColorMobile: colors.grey10,
                borderColor: colors.yellow,
                borderColorEmpty: 'transparent',
                borderColorHover: colors.navy,
                borderColorActive: colors.green,
                borderColorMobile: colors.grey10,
                textColor: colors.navy,
                textColorEmpty: colors.grey50,
                textColorHover: colors.white,
                textColorActive: colors.white,
                textColorMobile: colors.navy,
                textColorMobileActive: colors.white,
            },

            defaultMessages: {
                NextAvailability: 'Next availability is {nextAvailableDate, date, dateFormat}',
                NoAvailability: 'No upcoming appointments available',
                NoAvailabilityWithCta: 'Call {phoneNumber} for assistance',
                NoNewPatientAvailability: 'No upcoming availability for new patients',
                NoNewPatientAvailabilityWithCta: 'New patient? Call {phoneNumber} for assistance',
                PreviewDoctor: 'No appointment availability on Zocdoc',
                ViewExistingPatientAvailability: 'Existing patient? View availability',

                'NextAvailability-es': 'La siguiente disponibilidad es  {nextAvailableDate, date, dateFormat}',
                'NoAvailability-es': 'No hay próximas citas disponibles',
                'NoAvailabilityWithCta-es': 'Llame a {phoneNumber} para asistencia',
                'NoNewPatientAvailability-es': 'No hay próxima disponibilidad para nuevos pacientes',
                'NoNewPatientAvailabilityWithCta-es': '¿Paciente nuevo? Llame a {phoneNumber} para asistencia',
                'PreviewDoctor-es': 'No hay disponibilidad de citas en Zocdoc',
                'ViewExistingPatientAvailability-es': '¿Paciente existente? Ver disponibilidad',
            },

            dateFormat: {
                day: 'numeric',
                month: 'short',
                weekday: 'short',
            },
        },

        button: {
            primary: {
                bgColor: colors.yellow,
                borderColor: colors.yellow,
                color: colors.navy,
                hoverActiveBgColor: colors.navy,
                hoverActiveBorderColor: colors.navy,
                hoverActiveColor: colors.white,
            },
            inverted: {
                bgColor: colors.navy,
                borderColor: colors.navy,
                color: colors.white,
                hoverActiveBgColor: colors.yellow,
                hoverActiveBorderColor: colors.yellow,
                hoverActiveColor: colors.navy,
            },
            scroll: {
                bgColor: 'transparent',
                textColor: colors.navy,
            },
        },

        CollapsibleSection: {
            header: {
                arrowSize: 10.1587301587,
                bgColor: colors.white,
                bgColorExpanded: colors.white,
                textColor: colors.navy,
                textColorExpanded: colors.navy,
                fontSize: 18,
                height: 56,
            },

            children: {
                bgColor: colors.white,
                borderColor: colors.grey10,
                textColor: colors.navy,
            },
        },

        CollapsibleText: {
            textColor: colors.navy,
            linkColor: colors.blueLink,
        },

        GoogleMap: {
            markerColor: colors.navy,
            markerColorHover: colors.pink,
            markerColorActive: colors.coral,
            useZocdocMapStyle: true,
        },

        GoogleStaticMap: {
            markerColor: colors.coral,
            useZocdocMapStyle: true,
        },

        InsuranceCta: {
            iconColor: colors.purple,
            iconColorHover: colors.purple,
            textColor: colors.purple,
            textColorHover: colors.purple,
        },

        PrivacyUpdateBanner: {
            bgColor: colors.navy,
            textColor: colors.white,

            termsLink: {
                uri: 'https://www.zocdoc.com/about/terms/',
            },
        },

        SeoBlurb: {
            enabled: true,
        },

        SeoBreadcrumbLinks: {
            enabled: false,
        },

        SeoHeader: {
            enabled: true,
            bgColor: colors.white,
            linkColor: colors.blueLink,
            linkColorHover: colors.blueLink,
            linkColorVisited: colors.blueLink,
            linkUnderlineColor: colors.blueLink,
            linkUnderlineColorHover: colors.blueLink,
            linkUnderlineColorVisited: colors.blueLink,
            textColor: colors.navy,
        },

        SeoRelatedLinks: {
            enabled: false,
        },

        Spinner: {
            maskColor: 'rgba(255, 255, 255, 0.5)',
            segmentColor: colors.yellow,
            trackColor: colors.grey10,
        },

        Stars: {
            filledColor: colors.coral,
            unfilledColor: colors.grey25,
        },

        TakeoverInput: {
            option: {
                textColor: colors.navy,
                bgColorHover: colors.pink25,
            },
            highlightText: {
                textColor: colors.coral,
                textColorHover: colors.coral,
            },
            input: {
                textColor: colors.navy,
                textColorHover: colors.navy,
                textColorFocus: colors.navy,
            },
            placeholder: {
                textColor: colors.grey75,
                textColorHover: colors.grey75,
                textColorFocus: colors.grey25,
            },
        },

        TimesGrid: {
            header: {
                bgColor: colors.navy,
                textColor: colors.white,
            },

            slot: {
                bgColor: colors.yellow,
                bgColorEmpty: 'transparent',
                bgColorHover: colors.navy,
                bgColorActive: colors.navy,
                borderColor: colors.yellow,
                borderColorEmpty: 'transparent',
                borderColorHover: colors.navy,
                borderColorActive: colors.navy,
                textColor: colors.navy,
                textColorEmpty: colors.navy,
                textColorHover: colors.white,
                textColorActive: colors.white,
            },
        },
    },

    header: {
        bgColor: colors.yellow,
        bottomBorderColor: 'transparent',
        ctaLinkColor: colors.navy,
        ctaLinkColorHover: colors.white,
        linkColor: colors.navy,
        linkColorHover: colors.navy,
        linkColorVisited: colors.navy,
        linkUnderlineColor: colors.navy,
        linkUnderlineColorHover: colors.navy,
        linkUnderlineColorVisited: colors.navy,
        textColor: colors.navy,

        usernameDropdown: {
            labelTextColor: colors.navy,
            labelBgColor: colors.white,
            labelActiveTextColor: colors.white,
            labelActiveBgColor: colors.navy,
            itemTextColor: colors.navy,
            itemBgColor: colors.white,
            itemHoverBgColor: colors.grey10,
        },

        ctaLink: {
            text: 'List your practice on Zocdoc',
            uri: 'https://www.zocdoc.com/join',
        },

        'ctaLink-es': {
            text: 'Enumere su práctica en Zocdoc',
            uri: 'https://www.zocdoc.com/join',
        },

        rightContent: [{
            text: 'Sign in / Join',
            uri: 'https://www.zocdoc.com/signin',
        }],

        'rightContent-es': [{
            text: 'Iniciar sesión/ Registrarse',
            uri: 'https://www.zocdoc.com/signin',
        }],

        showLoginDropdown: false,

        logo: {
            linkUri: '/',
            uri: '',
            transparentUri: '',
            sizing: {
                // all except the first one are optional
                // change the sizing as desired per breakpoint
                // [height, width] tuple
                xxsmall: [20, 100],
                xsmall: null,
                small: null,
                medium: null,
                large: null,
                xlarge: null,
                xxlarge: null,
            },
        },

        mobileMenu: {
            enabled: true,

            home: {
                bgColor: colors.navy,
                borderColor: '#878791',
                textColor: colors.white,
            },

            links: [],

            signIn: {
                enabled: true,
            },
        },

        showPoweredByZocdoc: false,
        showPoweredByZocdocAsLink: true,
    },

    footer: {
        legal: {
            bgColor: colors.navy,
            linkColor: colors.white,
            linkColorHover: colors.white,
            linkColorVisited: colors.white,
            linkUnderlineColor: '#878791',
            linkUnderlineColorHover: '#878791',
            linkUnderlineColorVisited: '#878791',
            textColor: colors.grey75,

            leftContent: [{
                text: '© 2018 Zocdoc, Inc.',
                componentName: 'Zocdoc',
                textColor: colors.white,
                uri: '/',
            }, {
                text: 'Privacy',
                uri: '/about/privacypolicy/',
                componentName: 'Zocdoc',
            }, {
                text: 'Terms of Use',
                uri: '/about/terms/',
                componentName: 'Zocdoc',
            }, {
                text: 'Español',
                uri: 'https://espanol.zocdoc.com/',
                componentName: 'Zocdoc',
            }],

            'leftContent-es': [{
                text: '© 2018 Zocdoc, Inc.',
                componentName: 'Zocdoc',
                textColor: colors.white,
                uri: '/',
            }, {
                text: 'Política de Privacidad',
                uri: '/about/privacypolicy/',
                componentName: 'Zocdoc',
            }, {
                text: 'Términos de Uso',
                uri: '/about/terms/',
                componentName: 'Zocdoc',
            }, {
                text: 'English',
                uri: 'https://www.zocdoc.com/',
                componentName: 'Zocdoc',
            }],

            rightContent: null,

            showZocdocSocialLinks: true,
        },
        showLegal: true,

        showPricingLink: true,
        showSEO: true,
        seo: {
            bgColor: colors.navy,
            linkColor: colors.white,
            linkColorHover: colors.white,
            linkColorVisited: colors.white,
            linkUnderlineColor: '#878791',
            linkUnderlineColorHover: '#878791',
            linkUnderlineColorVisited: '#878791',
            textColor: colors.yellow,
        },

        showSupport: true,
        support: {
            bgColor: colors.yellow,
            linkColor: colors.navy,
            linkColorHover: colors.navy,
            linkColorVisited: colors.navy,
            linkUnderlineColor: colors.navy,
            linkUnderlineColorHover: colors.navy,
            linkUnderlineColorVisited: colors.navy,
            textColor: colors.navy,

            showZocdocIcon: true,

            leftContent: [{
                text: 'Need help?',
            }, {
                text: ' ',
            }, {
                element: 'VisitOur',
                text: 'Visit our',
            }, {
                element: 'KnowledgeBase',
                uri: 'https://support.zocdoc.com/Patients/s/',
                text: 'Knowledge base',
                bold: true,
            }],

            'leftContent-es': [{
                text: '¿Necesitas ayuda?',
            }, {
                text: ' ',
            }, {
                element: 'VisitOur',
                text: 'Visita nuestra',
            }, {
                element: 'KnowledgeBase',
                uri: 'https://support.zocdoc.com/Patients/s/',
                text: 'base de Conocimiento',
                bold: true,
            }],

            rightContent: null,
        },
    },

    /**
     * PAGES
     */

    search: {
        bar: {
            button: {
                bgColor: colors.navy,
                bgColorHover: colors.blue,
                bgColorPressed: colors.blueLink,
                iconColor: colors.white,
                iconColorHover: colors.white,
                iconColorPressed: colors.white,
            },

            field: {
                backgroundColor: colors.white,
                backgroundColorFocus: colors.white,
                borderColor: 'transparent',
                borderColorFocus: 'transparent',
                color: colors.navy,
                colorFocus: colors.navy,
                placeholderColor: colors.grey75,
                placeholderColorFocus: colors.grey25,
            },

            mobileCollapseArrow: {
                bgColor: colors.navy,
            },
        },

        config: {
            defaultAddress: undefined,
            defaultInsuranceCarrierId: undefined,
            defaultInsurancePlanId: undefined,
            defaultMapLatitude: undefined,
            defaultMapLongitude: undefined,
            openDesktopRefinePanelOnLoad: false,

            sortingOptions: [
                SortingOptions.Default,
                SortingOptions.Distance,
                SortingOptions.WaitTime,
            ],
        },

        /**
         * A string, with markdown syntax limited to bold/italic/link.
         * @type {String|undefined}
         * @example
         *
         * disclaimer: 'I just love sunny days, especially those in [California](http://www.ca.gov "Learn more about the state of California").'
         */
        disclaimer: undefined,

        header: {
            filters: {
                bgColor: colors.grey10,
                linkColor: colors.navy,
                linkColorHover: colors.navy,
                linkColorVisited: colors.navy,
                toggleColor: colors.grey,
                toggleColorActive: colors.navy,
                textColor: colors.navy,

                defaultSortOrder: 0,

                panel: {
                    header: {
                        bgColor: colors.grey,
                        textColor: colors.white,
                    },

                    section: {
                        bgColor: colors.white,
                        textColor: colors.navy,
                    },
                },
            },
        },

        results: {
            noResultsCopy: '### No Results\n\nNew doctors join Zocdoc every day, so try again soon!',
            'noResultsCopy-es': '### No Resultados\n\nNuevos médicos se unen al Zocdoc todos los días, ¡así que vuelve a intentarlo pronto!',

            // search has a ton of timesgrids on the page, so we have different
            // coloring behavior for it such as to not overwhelm the user
            timesGridOverrides: {
                // idle refers to the whole search card not being hovered on
                idle: {
                    bgColor: colors.grey10,
                    bgColorEmpty: 'transparent',
                    borderColor: colors.grey10,
                    borderColorEmpty: 'transparent',
                    textColor: colors.navy,
                    textColorEmpty: colors.grey75,
                },
                // active refers to the whole search card being hovered upon
                active: {
                    bgColor: colors.yellow,
                    bgColorEmpty: 'transparent',
                    bgColorHover: colors.navy,
                    bgColorActive: colors.green,
                    borderColor: colors.yellow,
                    borderColorEmpty: 'transparent',
                    borderColorHover: colors.navy,
                    borderColorActive: colors.green,
                    textColor: colors.navy,
                    textColorEmpty: colors.grey75,
                    textColorHover: colors.white,
                    textColorActive: colors.white,
                },
            },
        },

        footer: {
            pagination: {
                bgColor: colors.white,
                bgColorHover: colors.grey25,
                bgColorPressed: colors.grey25,
                textColor: colors.navy,
                textColorHover: colors.navy,
                textColorPressed: colors.navy,
            },
        },

        showInstitutes: false,
        showCommunityLabel: false,
    },

    specialtyDirectory: {
        enabled: false,

        header: {
            bgColor: colors.grey10,
            linkColor: colors.navy,
            linkActiveUnderlineColor: colors.yellow,
            textColor: colors.navy,
        },
    },

    practice: {
        textColor: colors.navy,
        linkColor: colors.blueLink,
    },

    profile: {
        allowedStatuses: [2],
        textColor: colors.navy,
        iBubblesText: {
            Name: 'Name of the provider. This information is provided to the health system by the provider.',
            Specialties: 'Specialties that the provider practices. This information is provided to the health system by the provider and updated periodically.',
            InNetworkInsurance: 'The insurances listed may not be accepted by this provider at all office locations, may be incomplete, or may have changed.',
            Practice: 'Network medical groups that this provider uses. This information is provided to the health system by the provider and updated periodically.',
            HospitalAffiliations: 'Network hospitals that this provider uses. This information is provided to the health system by the provider and updated periodically.',
            Certifications: 'Board certifications that this provider holds. This information is provided to the health system by the provider and updated periodically. For more information about board certification, please go to the American Board of Medical Specialties Web site at www.abms.org or go to www.osteopathic.org to visit the American Osteopathic Association\'s website.',
            Sex: 'Whether the provider is male or female. This information is provided to the health system by the provider and updated periodically.',
            Languages: 'Languages spoken in the provider\'s office.',
            NPI: 'National Provider Identifiers (NPIs) are used to identify healthcare providers in a standard way throughout the United States.',
            OfficeLocations: 'The location where the provider practices and the contact information for the location. This information is provided to the health system by the provider and updated periodically.',
            Badges: 'Zocdoc recognizes practices for notable and outstanding performance. Awards include:\n\nSee you again -\nPatients often return to this practice.\n\nRapid registration -\nThis practice offers online check-in, which allows patients to submit forms online before arriving for their appointment.\n\nSpeedy response -\nThis practice confirms appointments quickly.\n\nScheduling hero -\nThis practice keeps their schedules and insurances up to date.',
        },
        sectionDefaultValue: {
            Titles: null,
            Statement: null,
            Specialties: null,
            InNetworkInsurance: null,
            Practice: 'N/A',
            HospitalAffiliations: 'N/A',
            Institutes: null,
            Certifications: 'N/A',
            Education: null,
            Memberships: null,
            Awards: null,
            Badges: null,
            Sex: 'N/A',
            Languages: 'N/A',
            NPI: null,
        },
        showInstitutes: false,
        showCommunityLabel: false,
        showPhoneNumberIfNotBookableOrNotAvailable: true,
        unbookableSection: {
            markdown: `<#ifdef location.phone>
*[Call {{ location.phone }} for assistance](tel:{{ location.unformattedPhone }}){inverted}
<#endif>
`,
        },
        notAvailableText: 'This doctor has no appointment availability on Zocdoc.',
        'notAvailableText-es': 'Este médico no tiene disponibilidad de citas en Zocdoc.',
        linkColor: colors.blueLink,
        config: {
            defaultInsuranceCarrierId: undefined,
            defaultInsurancePlanId: undefined,
        },
        showPracticeLinks: false,
        definitions: {
            default: {
                true: 'Yes',
                false: 'No',
            },
            english: {
                true: 'Yes',
                false: 'No',
            },
        },
        locationInfo: {
            markdown:
`<#ifTrue location.isPrimaryCareProvided>
\`Care and/or services offered by provider may vary based on location. Please contact office for details.\`
<#endif>

<#ifdef location.phone>
### Phone
{{ location.phone }}
<#endif>

<#ifdef location.fax>
#### Fax
{{ location.fax }}
<#endif>

<#ifdef location.officeHoursDict>
### Office Hours
##### mon
{{ location.officeHoursDict.monday }}

##### tues
{{ location.officeHoursDict.tuesday }}

##### wed
{{ location.officeHoursDict.wednesday }}

##### thurs
{{ location.officeHoursDict.thursday }}

##### fri
{{ location.officeHoursDict.friday }}

##### sat
{{ location.officeHoursDict.saturday }}

##### sun
{{ location.officeHoursDict.sunday }}
<#endif>

<#ifdef location.isHandicapAccessible>
### Handicap accessible ![Handicap accessibility. This information is provided to the Health Plan by the provider and validated at least every three years or more frequently as updates are provided.](Tooltip.Accessibility "handicap accessible")
{{ location.isHandicapAccessible }}
<#endif>

<#ifdef location.isWheelchairAccessible>
#### Wheelchair accessible ![Wheelchair accessibility. This information is provided to the Health Plan by the provider and validated at least every three years or more frequently as updates are provided.](Tooltip.Accessibility "wheelchair accessible")
{{ location.isWheelchairAccessible }}
<#endif>

<#ifdef insurance.tier>
### Tier ![The provider, location, and insurance plan are in this cost tier. This information is provided to the Health Plan by the provider and validated at least every three years or more frequently as updates are provided.](Tooltip.Info "tier")
{{ insurance.tier }}
<#endif>

<#ifdef location.isProvenHealthNavigator>
### ProvenHealth Navigator Location ![This location offers specialized care and service to Geisinger Health Plan (GHP) members. With some of our plans such as GHP Extra, you may have a lower copayment for office visits at this location. Check your benefit documents to find out if you qualify.](Tooltip.Info "proven health navigator location")
{{ location.isProvenHealthNavigator  }}
<#endif>

<#ifdef location.isExtraLocation>
#### GHP Extra site ![This site qualifies for a lower Primary Care Physician (PCP) copay if your benefit plan includes one. See your ID card or Schedule of Benefits for details.](Tooltip.Info "geisinger health plan extra")
{{ location.isExtraLocation  }}
<#endif>

<#ifdef insurance.acceptsNewPatients>
### Accepts new patients ![Whether the provider is accepting new patients, existing patients only or is closed to new membership. This information is provided to the Health Plan by the provider and validated at least every three years or more frequently as updates are provided.](Tooltip.Info "accepting new patients")
{{ insurance.acceptsNewPatients }}
<#endif>

<#ifdef insurance.patientAgeRestrictions>
#### Patient age restriction ![Age range of patients the doctor sees. This information is provided to the Health Plan by the provider and validated at least every three years or more frequently as updates are provided.](Tooltip.Info "patient age restriction")
{{ insurance.patientAgeRestrictions }}
<#endif>
`,
        },
    },
};

export const generateBrandClientConfig = createBrandConfigGenerator(brandClientConfig);
